import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {useTranslation} from 'react-i18next';
import {Location} from 'history';
import {callNativeWeb, footerShowEventWeb, hideProgressWeb, showProgressWeb} from 'common/appInterface';
import Modal from "components/Modal";
import client from "lib/CommonApi/client";
import StringUtils from "common/stringUtils";
import useHistoryState from "use-history-state";

type CommonCodeRVO = {
	groupCode: string;
	codeName: string;
	mappingCodeValue: string;
	codeValue: string;
	etcType1: string;
}

const TerminateReason: React.FC = () => {
	/** Location State */
	const location = useLocation() as Location<LocationState>;
	/** Navigate */
	const navigate = useNavigate();
	/** 다국어처리 */
	const {t, i18n} = useTranslation();
	
	const screenId = 'SERV20';
	const screenName = t('SERV20.title');
	
	const [terminateReasonList, setTerminateReasonList] = useState<Array<CommonCodeRVO>>([]);
	
	const [bTerminateModal, setBTerminateModal] = useState<boolean>(false);
	
	const [checkedReasonList, setCheckedReasonList] = useHistoryState<Array<string>>([], "checkedReasonList")
	const [otherReason, setOtherReason] = useHistoryState<string>("", "otherReason");
	
	const openTerminateModal = () => {
		setBTerminateModal(true);
	}
	
	const closeTerminateModal = () => {
		setBTerminateModal(false);
	}
	
	const onCheckHandler = (value: string) => {
		setCheckedReasonList(
			checkedReasonList.includes(value)
				? checkedReasonList.filter(v => v !== value)
				: [...checkedReasonList, value],
			true
		)
	};
	
	const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setOtherReason(event.target.value, true);
	}
	
	const inquiryTerminateReasonListApi = useCallback(() => {
		showProgressWeb();
		
		client.get('/api/app/comn/codeList', {
			params: {
				groupCode: "00116",
				language: location.state.query.languageCode
			}
		}).then((response) => {
			if (response.data.code === "00") {
				setTerminateReasonList(response.data.response);
			}
		}).finally(() => {
			hideProgressWeb();
		});
	}, [location.state.query.languageCode]);
	
	const clickCancel = useCallback(() => {
		navigate("/main", {
			state: {
				query: location.state.query,
				swiperIndex: location.state.swiperIndex,
				tabIndex: location.state.tabIndex,
				from: location.pathname
			}
		});
	}, [navigate, location]);
	
	const clickTerminate = useCallback(() => {
			navigate('/manage/pincode/verify', {
				state: {
					query: location.state.query,
					swiperIndex: location.state.swiperIndex,
					tabIndex: location.state.tabIndex,
					info: location.state.info,
					apply: {
						alternativeCardNo: location.state.info.alternativeCardNo,
						callPage: "cardTerminate"
					},
					from: location.pathname,
					storage: {
						terminate: {
							alternativeCardNo: location.state.info.alternativeCardNo,
							reasons: [
								...checkedReasonList.map(v => ({code: v})),
								...(StringUtils.checkValueNull(otherReason) ? [] : [{code: "99", details: otherReason}])
							]
						}
					}
				}
			});
		},
		[navigate, location, checkedReasonList, otherReason]
	);
	
	/** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
	const callbackBackKeyEvent = useCallback(() => {
			navigate(-1);
		}, [navigate]
	);
	
	// 최초실행
	useEffect(() => {
		// 언어설정
		i18n.changeLanguage(location.state.query.languageCode);
		// callNative_Web 처리 (WEB > APP)
		callNativeWeb(screenId, screenName, location.state.from === '/intro' ? 'Y' : 'N');
		// 하단바 숨기기
		footerShowEventWeb('N');
		
		console.log('QUERY STATE');
		console.log(location.state);
		
		inquiryTerminateReasonListApi();
		
		// 최초한번실행 useEffect() Warning 제거
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	
	// 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
	useEffect(() => {
		document.addEventListener('backpress', callbackBackKeyEvent);
		return () => document.removeEventListener('backpress', callbackBackKeyEvent);
	}, [callbackBackKeyEvent]);
	
	return (
		<section className="pdb70">
			<div id="container">
				<section className="terminate">
					<div className="content">
						<span className="title">{t('SERV20.subTitle01')}</span>
					</div>
					<div className="agreeBox">
						{
							terminateReasonList.filter(
								value => value.codeValue !== "99"
							).map((terminateReason: CommonCodeRVO, index: number) => (
								<div className="checkbox1" key={"terminateReason" + index}>
									<div className="checkboxInner">
										<input type="checkbox" id={"reason" + terminateReason.codeValue}
											   checked={checkedReasonList.includes(terminateReason.codeValue)}
											   onChange={() => onCheckHandler(terminateReason.codeValue)}
											   value={terminateReason.codeValue}
										/>
										<label htmlFor={"reason" + terminateReason.codeValue}>
											<span className="text2">
												{t(`SERV20.reason.${terminateReason.codeValue}`)}
											</span>
										</label>
									</div>
								</div>
							))
						}
					</div>
					<div className="divider"/>
					<div className="content">
						<span className="text2">{t('SERV20.reason.99')}</span>
						<textarea className="textarea text2 mgt8" maxLength={160}
								  value={otherReason} onChange={onChangeHandler}
						/>
					</div>
				</section>
				<div className="btnArea">
					<div className="btnWrapTy01">
						<button type="button" className="btnPrimaryTy01" onClick={openTerminateModal}
								disabled={checkedReasonList.length === 0 && StringUtils.checkValueNull(otherReason)}
						>
							<span>{t("COMMON.button.terminate")}</span>
						</button>
					</div>
				</div>
			</div>
			<Modal isOpen={bTerminateModal} contentLabel="modal" onRequestClose={closeTerminateModal}
				   className="popTyLayer2 terminate" overlayClassName="dim" closeTimeoutMS={500}
			>
				<p className="title">{t("SERV20.popup.title")}</p>
				<p className="text2 mgt20 mgb0">{t("SERV20.popup.subTitle")}</p>
				<div className="subContent">
					<dd className="text2 mgt0 mgb0">{t("SERV20.popup.explain01")}</dd>
					<dd className="text2 mgt0 mgb0">{t("SERV20.popup.explain02")}</dd>
					<dd className="text2 mgt0 mgb0">{t("SERV20.popup.explain03")}</dd>
				</div>
				<div className="btnWrapTy02">
					<button type="button" className="btnPrimaryTy03" onClick={clickCancel}>
						<span>{t("COMMON.button.cancel")}</span>
					</button>
					<button type="button" className="btnPrimaryTy01" onClick={clickTerminate}>
						<span>{t("COMMON.button.terminate")}</span>
					</button>
				</div>
			</Modal>
		</section>
	);
};

export default TerminateReason;